import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { MdModeEdit } from "react-icons/md";
import {
  useAddServiceTypeMutation,
  useUpdateServiceTypeMutation,
  useGetServiceTypeByIdQuery,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import { Plus } from "lucide-react";
import GenericDropdown from "../../components/GenericDropDown";
import SkeletonAddEditCategoryDialog from "../../skeletonLoading/SkeletonAddEditCategoryDialog";
import {
  MAX_CATEGORYNAME_LENGTH_MAX,
  MAX_SERVICETYPE_NAME_LENGTH_MAX,
} from "../../lib/constants";
import SortupDialog from "../../components/common/SortupDialog";

interface ServiceType {
  name: string;
  isDistanceRangApplicable: boolean;
}

interface AddEditServiceTypeDialogProps {
  isEditOnly: boolean;
  serviceTypeId?: number;
  setPageNumber: Dispatch<SetStateAction<number>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  fetchData: () => Promise<void>;
}

export function AddEditServiceTypeDialog({
  serviceTypeId,
  isEditOnly,
  fetchData,
  setSearchTerm,
  setPageNumber,
}: AddEditServiceTypeDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [servicetypeValues, setServicetypeValues] = useState<ServiceType>({
    name: "",
    isDistanceRangApplicable: false,
  });
  const [isUniqueError, setIsUniqueError] = useState(false);

  const toast = useToast();
  const { data, isLoading, isSuccess } = useGetServiceTypeByIdQuery(
    { id: serviceTypeId || 0 },
    { skip: !(isDialogOpen && isEditOnly) }
  );

  const [AddServiceType, { isLoading: isServiceTypesLoading }] =
    useAddServiceTypeMutation();
  const [UpdateServiceType, { isLoading: isUpdateServiceTypeLoading }] =
    useUpdateServiceTypeMutation();

  useEffect(() => {
    if (isSuccess && data) {
      setServicetypeValues({
        name: data.data.name,
        isDistanceRangApplicable: data.data.isDistanceRangApplicable,
      });
    }
  }, [isSuccess, data]);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
    if (!isEditOnly) {
      setServicetypeValues({
        name: "",
        isDistanceRangApplicable: false,
      });
      setIsUniqueError(false);
    }
  };

  const handleServiceTypeNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.slice(0, MAX_SERVICETYPE_NAME_LENGTH_MAX);
    setServicetypeValues((prev) => ({ ...prev, name: value }));

    if (isUniqueError) {
      setIsUniqueError(false);
    }
  };

  const handleServiceTypeDistanceRangeChange = (value: string) => {
    setServicetypeValues((prev) => ({
      ...prev,
      isDistanceRangApplicable: value === "Yes",
    }));
  };

  const handleAddServiceType = async () => {
    if (isServiceTypesLoading || isUniqueError) return;

    const response: any = await AddServiceType({
      addServiceTypeDto: {
        name: servicetypeValues.name,
        isDistanceRangApplicable: servicetypeValues.isDistanceRangApplicable,
      },
    });

    // Check for error in response
    if ("error" in response) {
      if (response.error?.data?.message?.includes("already exists")) {
        setIsUniqueError(true);
        handleCloseError("Service type name already exists");
      } else {
        handleCloseError(
          response.error?.data?.message || "Failed to add service type"
        );
      }
      return;
    }

    // Success case
    if (response?.data?.message?.includes("success")) {
      handleCloseSuccess(response.data.message);
    }
  };

  const handleUpdateServiceType = async () => {
    if (isUpdateServiceTypeLoading || isUniqueError) return;

    const response: any = await UpdateServiceType({
      updateServiceTypeDto: {
        name: servicetypeValues.name,
        isDistanceRangApplicable: servicetypeValues.isDistanceRangApplicable,
        id: serviceTypeId || 0,
      },
    });

    // Check for error in response
    if ("error" in response) {
      if (response.error?.data?.message?.includes("already exists")) {
        setIsUniqueError(true);
        handleCloseError("Service type name already exists");
      } else {
        handleCloseError(
          response.error?.data?.message || "Failed to update service type"
        );
      }
      return;
    }

    // Success case
    if (response?.data?.message?.includes("success")) {
      handleCloseSuccess(response.data.message);
    }
  };

  const handleCloseSuccess = (message: string) => {
    setIsDialogOpen(false);
    toast.toast({
      title: "Success!",
      description: message,
    });
    setPageNumber(1);
    fetchData();
  };

  const handleCloseError = (message: string) => {
    toast.toast({
      title: "Error!",
      description: message,
    });
  };

  const isFormDisabled =
    servicetypeValues.name === "" ||
    isServiceTypesLoading ||
    isUpdateServiceTypeLoading;

  return (
    <>
      <div className="flex items-center space-x-2">
        {isEditOnly ? (
          <div onClick={handleDialogOpen}>
            <MdModeEdit
              size={16}
              className="cursor-pointer text-[#72767E] hover:text-black"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDialogOpen}
            className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 gap-1 rounded-lg w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Add Service Type
          </button>
        )}
      </div>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header={isEditOnly ? "Edit Service Type" : "Add Service Type"}
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: isEditOnly ? "Update" : "Add",
            onClick: isEditOnly
              ? handleUpdateServiceType
              : handleAddServiceType,
            className: isFormDisabled
              ? "opacity-50 cursor-not-allowed text-white"
              : "text-white",
            disabled: isFormDisabled,
          },
          {
            name: "Cancel",
            className: "bg-white text-gray-900 border",
            disabled: isServiceTypesLoading || isUpdateServiceTypeLoading,
            onClick: () => setIsDialogOpen(false),
          },
        ]}
      >
        {isLoading ? (
          <SkeletonAddEditCategoryDialog rows={2} />
        ) : (
          <>
            <label
              htmlFor="serviceTypeName"
              className="block text-sm font-medium text-gray-700"
            >
              Service Type Name
            </label>
            <input
              type="text"
              id="serviceTypeName"
              value={servicetypeValues.name}
              onChange={handleServiceTypeNameChange}
              className={`focus:outline-customGreenColor mt-1 p-2 border ${
                isUniqueError ? "border-red-500" : "border-gray-300"
              } rounded-md w-full`}
            />
            {isUniqueError && (
              <p className="text-red-500 text-sm mt-1">
                Service type name is not unique.
              </p>
            )}
            {isEditOnly ? <br /> : <> </>}
            <span className="text-xs text-gray-500">
              {servicetypeValues.name.length}/{MAX_SERVICETYPE_NAME_LENGTH_MAX}
            </span>
            <label
              htmlFor="isDistanceRange"
              className="block mt-4 text-sm font-medium text-gray-700 mb-1"
            >
              Distance Range Applicable
            </label>
            <GenericDropdown
              labelValue={
                servicetypeValues.isDistanceRangApplicable ? "Yes" : "No"
              }
              onChange={handleServiceTypeDistanceRangeChange}
              options={[
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ]}
              placeholder="Select Distance Range Option"
              error={false}
              className="w-full justify-between"
              scrollHeight=""
              dropdownMenuContentWidth="w-[34vw]"
            />
          </>
        )}
      </SortupDialog>
    </>
  );
}
